import { useMemo } from 'react';
import type { UseQueryResult } from '@edreamsodigeo/frontend-api-client';
import { useQuery } from '@edreamsodigeo/frontend-api-client';

import type {
  GetUserAccountQueryVariables,
  GetUserAccountQuery,
} from '@/generated/graphql';
import { GET_USER_ACCOUNT_INFO } from '@/clientState/providers/operations/userAccountInfo';
import useShoppingBundleInfo from '@/clientState/hooks/useShoppingBundleInfo';
import { fillEmailToUserTravellers } from '@/clientState/commonFunctions';

const getTravellersWithEmail = (
  userDataInfo: GetUserAccountQuery
): GetUserAccountQuery => {
  if (!userDataInfo?.userAccount) {
    return userDataInfo;
  }

  const userTravellers = fillEmailToUserTravellers(userDataInfo);
  return {
    ...userDataInfo,
    userAccount: {
      ...userDataInfo?.userAccount,
      userTravellers,
    },
  } as GetUserAccountQuery;
};

export interface UserAccountInfo {
  errors: UseQueryResult<
    GetUserAccountQuery,
    GetUserAccountQueryVariables
  >['errors'];
  loading: UseQueryResult<
    GetUserAccountQuery,
    GetUserAccountQueryVariables
  >['loading'];
  data: UseQueryResult<
    GetUserAccountQuery,
    GetUserAccountQueryVariables
  >['data'];
}

const useUserAccountInfo = (): UserAccountInfo => {
  const shoppingBundleInfo = useShoppingBundleInfo();
  const { data, loading, errors } = useQuery<
    GetUserAccountQuery,
    GetUserAccountQueryVariables
  >(GET_USER_ACCOUNT_INFO, {
    variables: {
      userAccountRequest: {
        shoppingInfo: shoppingBundleInfo,
      },
    },
  });

  const dataWithEmail: GetUserAccountQuery = useMemo(
    () => getTravellersWithEmail(data as GetUserAccountQuery),
    [data]
  );

  return {
    data: dataWithEmail,
    loading,
    errors,
  };
};

export default useUserAccountInfo;
